import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Carteirinha } from "@/components/Carteirinha";
import { Download, Share2 } from "lucide-react";
import { toPng, toBlob } from "html-to-image";
import { toast } from "sonner";

export default function ClassCards() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [classId, setClassId] = useState("");
  const [statsMap, setStatsMap] = useState({});
  const gridRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/classes"), api.get("/students")]);
        setClasses(c.data);
        setStudents(s.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const list = useMemo(() => students.filter(s => s.class_id === classId), [students, classId]);

  useEffect(() => {
    if (!list.length) return;
    (async () => {
      const results = await Promise.all(list.map(async (s) => {
        try {
          const [att, evs] = await Promise.all([
            api.get(`/attendance/student/${s.id}`),
            api.get(`/evaluations/student/${s.id}`),
          ]);
          const scores = evs.data.flatMap(e => Object.values(e.scores || {}));
          const avg = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : "—";
          return [s.id, { attendance: att.data.percentage, evals: evs.data.length, avgScore: avg }];
        } catch { return [s.id, {}]; }
      }));
      setStatsMap(Object.fromEntries(results));
    })();
  }, [list]);

  const downloadAll = async () => {
    if (!gridRef.current) return;
    try {
      const dataUrl = await toPng(gridRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0b1320" });
      const link = document.createElement("a");
      const klass = classes.find(c => c.id === classId);
      link.download = `carteirinhas-${(klass?.name || "turma").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Imagem baixada");
    } catch {
      toast.error("Falha ao gerar imagem");
    }
  };

  const shareAll = async () => {
    if (!gridRef.current) return;
    try {
      const blob = await toBlob(gridRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0b1320" });
      if (!blob) throw new Error("blob");
      const klass = classes.find(c => c.id === classId);
      const file = new File([blob], `carteirinhas-${klass?.name || "turma"}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Carteirinhas — ${klass?.name}` });
      } else if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("Imagem copiada! Cole no WhatsApp ou Instagram.");
      } else {
        await downloadAll();
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Compartilhamento não suportado");
    }
  };

  return (
    <div data-testid="admin-class-cards-page">
      <PageHeader
        eyebrow="Compartilhamento"
        title="Carteirinhas da turma"
        subtitle="Gere um painel com todas as carteirinhas para compartilhar em redes sociais."
        mascot={BRAND.DUO_ACAO}
      />

      <div className="vv-card p-4 mb-5 flex flex-col sm:flex-row gap-3 items-end">
        <div className="flex-1 w-full">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Turma</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="classcards-class-select"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
            <SelectContent>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name} • {c.category}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={downloadAll} disabled={!list.length} className="bg-[hsl(var(--vv-blue))] font-bold uppercase" data-testid="classcards-download-button">
          <Download className="size-4 mr-2" /> Baixar PNG
        </Button>
        <Button onClick={shareAll} variant="outline" disabled={!list.length} data-testid="classcards-share-button">
          <Share2 className="size-4 mr-2" /> Compartilhar
        </Button>
      </div>

      {!classId ? (
        <EmptyState title="Selecione uma turma" description="Depois de escolher a turma, todas as carteirinhas dos alunos aparecerão abaixo." mascot={BRAND.MANGUITO} />
      ) : list.length === 0 ? (
        <EmptyState title="Turma sem alunos" description="Nenhum aluno está associado a esta turma." />
      ) : (
        <div ref={gridRef} className="p-6 rounded-xl" style={{ background: "#0b1320" }}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={BRAND.LOGO} alt="" className="h-14" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--vv-lime))]">Turma</div>
                <div className="text-2xl font-black text-white" style={{ fontFamily: "'Archivo Black', sans-serif" }}>
                  {classes.find(c => c.id === classId)?.name}
                </div>
              </div>
            </div>
            <div className="text-white/70 text-sm">
              {list.length} atleta(s) • Vôlei Vida · Jardinópolis
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 justify-items-center">
            {list.map(s => (
              <Carteirinha key={s.id} student={s} stats={statsMap[s.id] || {}} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
